import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { Selection } from "../selection/Selection";
import type { Command } from "./Command";
import { setPos as setLayerPos } from "./MoveLayerCommand";

// MoveLayerAndSelectionCommand — move a layer AND translate the selection in
// one atomic, undoable step.
//
// Used by MoveTool when committing a float+move: the floated layer moves to
// its destination, and the selection marquee marches with it so the user sees
// the selection around the moved pixels (Photoshop behaviour).
//
// `coalesceWith` returns true for the FloatSelectionCommand that preceded this
// move, so the whole float+move gesture collapses into ONE undo entry.

/** An additional (non-floating) selected/linked layer moved in the same gesture. */
export interface ExtraLayerMove {
  readonly id: string;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

export class MoveLayerAndSelectionCommand implements Command {
  readonly kind = "move-layer-and-selection";

  constructor(
    private readonly layerId: string,
    private readonly fromX: number,
    private readonly fromY: number,
    private readonly toX: number,
    private readonly toY: number,
    /** Selection BEFORE the move (from the state after floating). */
    private readonly selectionBefore: Selection | null,
    /** Selection AFTER the move (translated by the same delta). */
    private readonly selectionAfter: Selection | null,
    /**
     * Other selected/linked layers dragged alongside the floating primary
     * layer (audit i4/i56). They translate as whole layers — only the
     * primary layer floats/cuts pixels. Empty/omitted preserves the exact
     * single-layer float+move behaviour.
     */
    private readonly extraMoves: readonly ExtraLayerMove[] = [],
  ) {}

  apply(doc: Document): Document {
    let layers = setPos(doc.layers, this.layerId, this.toX, this.toY);
    for (const e of this.extraMoves) {
      layers = setLayerPos(layers, e.id, e.toX, e.toY);
    }
    return {
      ...doc,
      layers,
      selection: this.selectionAfter,
    };
  }

  invert(_before: Document, _after: Document): Command {
    return new MoveLayerAndSelectionCommand(
      this.layerId,
      this.toX,
      this.toY,
      this.fromX,
      this.fromY,
      this.selectionAfter,
      this.selectionBefore,
      this.extraMoves.map((e) => ({
        id: e.id,
        fromX: e.toX,
        fromY: e.toY,
        toX: e.fromX,
        toY: e.fromY,
      })),
    );
  }

  /**
   * Coalesce with the FloatSelectionCommand that immediately preceded this
   * move so the whole float+move gesture is a single undo step.
   */
  coalesceWith(previous: Command): boolean {
    return previous.kind === "float-selection";
  }
}

function setPos(layers: readonly Layer[], id: string, x: number, y: number): Layer[] {
  return layers.map((l) => {
    if (l.id === id) return { ...l, x, y };
    if (l.kind === "group") return { ...l, children: setPos(l.children, id, x, y) };
    return l;
  });
}
